import React, { FC, useEffect, useState, useContext } from 'react'
import moment from 'moment'
import { useApolloClient } from '@apollo/client'
import ROUTES from '../../../../utils/routes'
import { QuestionMark, Button } from '../../../../System'
import { SkeletonLoader } from '../../../../SkeletonLoader'
import { userStore } from '../../../../utils/UserContext'
import { StyledPayouts } from '.'
import { AffiliateEarningFindBetweenDates } from '../../../../../graphql/AffiliateEarning/__generated__/AffiliateEarningFindBetweenDates'
import { affiliateEarningFindBetweenDates } from '../../../../../graphql/AffiliateEarning/affiliateEarningFindBetweenDates'
import useAffiliateDashboard from '../../../AffiliateDashboard/useAffiliateDashboard'

const Payouts: FC = () => {
  const apollo = useApolloClient()
  const { user } = useContext(userStore)
  const { loading, foreverEarnings } = useAffiliateDashboard()

  const [nextPayout, setNextPayout] = useState(0)
  const [previousPayout, setPreviousPayout] = useState(0)

  const loadNextPayout = async () => {
    const oneMonthAgo = moment()
      .utc()
      .startOf('month')
      .toDate()
    const twoMonthsAgo = moment()
      .utc()
      .subtract(1, 'months')
      .startOf('month')
      .toDate()

    const { data } = await apollo.query<AffiliateEarningFindBetweenDates>({
      fetchPolicy: 'no-cache',
      query: affiliateEarningFindBetweenDates,
      variables: {
        affiliate: user?.id,
        greaterThanDate: twoMonthsAgo,
        lessThanDate: oneMonthAgo
      }
    })

    const affiliateEarnings = data.affiliateEarningFindMany
    const nextPayoutAmount = affiliateEarnings.reduce((total, earning) => {
      return total + earning.amount
    }, 0)

    setNextPayout(nextPayoutAmount)
  }

  const loadPreviousPayout = async () => {
    const twoMonthsAgo = moment()
      .utc()
      .subtract(1, 'months')
      .startOf('month')
      .toDate()
    const threeMonthsAgo = moment()
      .utc()
      .subtract(2, 'months')
      .startOf('month')
      .toDate()

    const { data } = await apollo.query<AffiliateEarningFindBetweenDates>({
      fetchPolicy: 'no-cache',
      query: affiliateEarningFindBetweenDates,
      variables: {
        affiliate: user?.id,
        greaterThanDate: threeMonthsAgo,
        lessThanDate: twoMonthsAgo
      }
    })

    const affiliateEarnings = data.affiliateEarningFindMany
    const previousPayoutAmount = affiliateEarnings.reduce((total, earning) => {
      return total + earning.amount
    }, 0)

    setPreviousPayout(previousPayoutAmount)
  }

  useEffect(() => {
    loadNextPayout()
    loadPreviousPayout()
  }, [])

  return (
    <StyledPayouts>
      <div className="heading">
        <h2>
          Payouts <QuestionMark tooltip="Payouts are processed on the first day of each month." />
        </h2>
        <p>You are enrolled in automatic payouts.</p>
      </div>

      <div className="payout-metrics">
        {loading && <SkeletonLoader type="caption-list-item" quantity={2} />}
        {!loading && (
          <>
            <div className="payout-metric">
              <span className="payout-value">${nextPayout.toFixed(2)}</span>
              <div className="payout-details">
                <div className="payout-main-date">
                  <span className="payout-label">NEXT PAYOUT</span>
                  <span className="payout-date">
                    {moment()
                      .utc()
                      .add(1, 'months')
                      .startOf('month')
                      .format('MMM. D YYYY')}
                  </span>
                </div>
                <hr />
                <div className="payout-earnings-date">
                  <span className="payout-label">FOR EARNINGS OF</span>
                  <span className="payout-date">
                    {moment()
                      .utc()
                      .subtract(1, 'months')
                      .startOf('month')
                      .format('MMM. YYYY')}
                  </span>
                </div>
              </div>
            </div>
            <div className="payout-metric">
              <span className="payout-value">${previousPayout.toFixed(2)}</span>
              <div className="payout-details">
                <div className="payout-main-date">
                  <span className="payout-label">PREVIOUS PAYOUT</span>
                  <span className="payout-date">
                    {moment()
                      .utc()
                      .startOf('month')
                      .format('MMM. D YYYY')}
                  </span>
                </div>
                <hr />
                <div className="payout-earnings-date">
                  <span className="payout-label">FOR EARNINGS OF</span>
                  <span className="payout-date">
                    {moment()
                      .utc()
                      .subtract(2, 'months')
                      .startOf('month')
                      .format('MMM. YYYY')}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <hr />

      <div className="total-earnings">
        <span className="earnings-label">Total earned since becoming an affiliate</span>
        <span className="earnings-value">${foreverEarnings.toFixed(2)}</span>
        <a href={ROUTES.external.payPalSignInUrl} target="_blank" rel="nooppener noreferrer">
          <Button className="paypal-btn" type="small">
            Go to My PayPal
          </Button>
        </a>
      </div>
    </StyledPayouts>
  )
}

export default Payouts
